import React, { type ChangeEvent, useCallback, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItemSearchable } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FilterGroupOptions } from '@rocco/types/filter-group-options';
import { useControllableValue, useDebounceFn } from 'ahooks';
import { Input as RoccoInput } from '@rocco/components/input';
import { isSearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';

export const FiltersModalSearchableList = (
    props: FilterModalElementProps<
        FilterConfigModalItemSearchable,
        SearchableListFilterState
    > & {
        onAlphabeticalGrouping: (args: AggregationArgs) => void;
        /** `sidebar` = compact 5.0 directory browse styling. */
        variant?: 'default' | 'sidebar';
        /** Flip colours for the dark (projects) browse theme. */
        isDark?: boolean;
        /**
         * Suppress the selected-item chips above the list. For hosts that
         * already surface selections elsewhere (the refine sheet's chips
         * row) — the checked rows in the list are representation enough.
         */
        hideSelectedChips?: boolean;
    },
) => {
    const {
        filterConfigItem,
        filterState,
        onFilterStateUpdate,
        onAlphabeticalGrouping,
        displayMode,
        variant = 'default',
        isDark = false,
        hideSelectedChips = false,
    } = props;
    const isSidebar = variant === 'sidebar';

    const { searchFilters } = useFiltersContext();
    const { handleAggregate } = searchFilters;

    const defaultShowCount = 60;
    const [showCount, setShowCount] = useState(defaultShowCount);
    const [isLoading, setIsLoading] = useState(false);

    const filterKey = filterConfigItem.filterKey;

    /** Gets updated state from aggregation, saving properties chosen by user **/
    const getUpdatedState = (state: SearchableListFilterState) => {
        const items = state.items.map((item) => {
            const selectedItem = state.selectedItems.find(
                (selectedItem) => selectedItem.value === item.value,
            );

            return selectedItem ? { ...selectedItem } : { ...item };
        });

        return { ...state, items };
    };

    const { run: searchAggregateRun } = useDebounceFn(
        (searchQuery: string) => {
            handleAggregate({
                aggregateType: 'searchListSearch',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        search: searchQuery,
                        after: filterState.after ?? undefined, // Search service wants `undefined`, not null
                        size: filterState.size,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: (data) => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        const newItems = newListState?.items ?? [];

                        onFilterStateUpdate(
                            getUpdatedState({
                                ...filterState,
                                search: searchQuery,
                                items: newItems,
                                after: newListState?.after ?? null,
                                hasMoreItems: !!newListState?.items.length,
                                isSearching: false,
                            }),
                            false,
                        );
                    }
                },
            });
        },
        { wait: 500 },
    );

    const searchGroupAlphabetically = useCallback(
        (groupBy: keyof FilterGroupOptions | undefined) => {
            onAlphabeticalGrouping({
                aggregateType: groupBy
                    ? 'groupAlphabetical'
                    : 'searchListLoadMore',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        after: undefined,
                        size: 1000,
                        startsWith: groupBy,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: (data) => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        setShowCount(defaultShowCount);
                        onFilterStateUpdate(
                            getUpdatedState({
                                ...filterState,
                                search: '',
                                items: newListState?.items ?? [],
                                groupedBy: groupBy,
                            }),
                            false,
                        );
                    }
                },
            });
        },
        [
            filterConfigItem.aggregationProperty,
            filterKey,
            filterState,
            onAlphabeticalGrouping,
            onFilterStateUpdate,
        ],
    );

    const showMoreCount = filterState.items.length - showCount;
    const showItems = filterState.items.slice(0, showCount);

    const columns =
        displayMode === DisplayMode.Desktop &&
        filterConfigItem.columns &&
        filterConfigItem.columns > 0
            ? filterConfigItem.columns
            : 1;

    const containerStyles = {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${Math.ceil(showItems.length / columns)}, auto)`,
    };

    return (
        <div className={isSidebar ? undefined : 'pt-2'}>
            <SearchableList
                isLoading={isLoading}
                variant={variant}
                isDark={isDark}
                searchInputPlaceholder={filterConfigItem.searchInputPlaceholder}
                searchQuery={filterState.search}
                groupAlphabetically={
                    // The compact sidebar hides the A–Z "jump to letter" row.
                    isSidebar
                        ? false
                        : (filterConfigItem.allowAlphabeticalGrouping ?? false)
                }
                groupedBy={filterState.groupedBy}
                firstCharacterGroups={filterState.firstCharacterGroups}
                onGroupingChange={searchGroupAlphabetically}
                onSearchQueryChange={(updatedSearchQuery) => {
                    onFilterStateUpdate(
                        {
                            ...filterState,
                            search: updatedSearchQuery,
                            groupedBy: undefined,
                            isSearching: true,
                        },
                        false,
                    );

                    searchAggregateRun(updatedSearchQuery);
                }}
            >
                {/* Selected-item chips at the top. Hidden in the sidebar and
                    wherever the host surfaces selections itself — they
                    already show as checked rows in the list below. */}
                {!isSidebar &&
                    !hideSelectedChips &&
                    filterState.selectedItems.length > 0 && (
                        <div className="mb-10 flex flex-wrap gap-3">
                            {filterState.selectedItems.map((item, index) => (
                                <FiltersModalSearchableListItem
                                    key={index}
                                    item={item}
                                    filterState={filterState}
                                    onFilterStateUpdate={onFilterStateUpdate}
                                    isSelected={true}
                                    variant={variant}
                                    isDark={isDark}
                                />
                            ))}
                        </div>
                    )}

                <div
                    className={cn(
                        isSidebar
                            ? 'flex flex-col gap-1.5'
                            : 'grid grid-flow-col-dense gap-2',
                    )}
                    style={isSidebar ? undefined : containerStyles}
                >
                    {showItems.map((item, index) => (
                        <FiltersModalSearchableListItem
                            key={index}
                            item={item}
                            filterState={filterState}
                            onFilterStateUpdate={onFilterStateUpdate}
                            variant={variant}
                            isDark={isDark}
                        />
                    ))}
                </div>

                {showMoreCount > 0 && (
                    <div
                        className={cn(
                            'flex cursor-pointer flex-col items-center',
                            isSidebar ? 'mt-3' : 'mt-10',
                        )}
                        onClick={() => {
                            setShowCount(showCount + defaultShowCount);
                        }}
                    >
                        <span
                            className={cn(
                                'font-medium',
                                isSidebar
                                    ? cn(
                                          'text-sm',
                                          isDark
                                              ? 'text-white/80'
                                              : 'text-[#343434]',
                                      )
                                    : 'text-gray-900',
                            )}
                        >
                            Display{' '}
                            {showMoreCount < defaultShowCount
                                ? showMoreCount
                                : defaultShowCount}{' '}
                            more
                        </span>
                        <ChevronDown
                            className={isSidebar ? 'size-4' : 'size-4.5'}
                        />
                    </div>
                )}
            </SearchableList>
        </div>
    );
};

interface FiltersModalSearchableListItemProps {
    item: SingleValueFilterState;
    filterState: SearchableListFilterState;
    isSelected?: boolean;
    variant?: 'default' | 'sidebar';
    isDark?: boolean;
    onFilterStateUpdate: (
        state: SearchableListFilterState,
        shouldAggregate?: boolean,
    ) => void;
}

const FiltersModalSearchableListItem = (
    props: FiltersModalSearchableListItemProps,
) => {
    const {
        item,
        filterState,
        onFilterStateUpdate,
        isSelected = false,
        variant = 'default',
        isDark = false,
    } = props;
    const {
        searchFilters: { handleToggleSearchableList },
    } = useFiltersContext();

    const id = `${item.value}${isSelected ? '-selected' : ''}`;
    const isSidebar = variant === 'sidebar';

    const toggle = (checked: boolean | 'indeterminate') => {
        if (handleToggleSearchableList) {
            onFilterStateUpdate(
                handleToggleSearchableList(filterState, item, !!checked),
            );
        }
    };

    // Sidebar: left-aligned checkbox matching the rest of the 5.0 filter panel
    // (15px #343434 box, 14px label). Selected chips keep their pill outline.
    if (isSidebar && !isSelected) {
        return (
            <label className="flex cursor-pointer items-start gap-2">
                <RoccoCheckbox
                    id={id}
                    checked={item.checked}
                    onCheckedChange={toggle}
                    className={cn(
                        'mt-0.5 size-[0.9375rem] shrink-0 rounded-[0.125rem] border',
                        isDark
                            ? 'border-white/60 data-[state=checked]:border-white'
                            : 'border-[#343434]',
                    )}
                />
                <span
                    className={cn(
                        'text-[0.875rem] leading-[1.358]',
                        isDark ? 'text-white/80' : 'text-[#343434]',
                    )}
                >
                    {item.label}
                </span>
            </label>
        );
    }

    return (
        <div
            className={cn(
                'flex items-center',
                isSelected
                    ? 'rounded-2 border px-3 py-2'
                    : 'flex-row-reverse justify-between',
                isSelected && isDark
                    ? 'border-white/40 text-white'
                    : isSelected
                      ? 'border-gray-900'
                      : undefined,
            )}
        >
            <RoccoCheckbox
                id={id}
                checked={item.checked}
                onCheckedChange={toggle}
            />
            <RoccoLabel
                htmlFor={id}
                target={isSelected ? 'checkbox' : 'checkboxReversed'}
                singleLine
                className={isSidebar && isDark ? 'text-white' : undefined}
            >
                {item.label}
            </RoccoLabel>
        </div>
    );
};

interface SearchableListProps extends React.PropsWithChildren {
    isLoading: boolean;
    groupAlphabetically: boolean;
    searchInputPlaceholder: string;
    searchQuery?: string;
    onSearchQueryChange?: (searchQuery: string) => void;
    groupedBy?: keyof FilterGroupOptions;
    onGroupingChange?: (groupBy?: keyof FilterGroupOptions) => void;
    firstCharacterGroups?: Partial<Record<keyof FilterGroupOptions, number>>;
    variant?: 'default' | 'sidebar';
    isDark?: boolean;
}

const SearchableList = (props: SearchableListProps) => {
    const {
        children,
        isLoading,
        searchInputPlaceholder,
        groupedBy,
        onGroupingChange,
        groupAlphabetically,
        firstCharacterGroups,
        variant = 'default',
        isDark = false,
    } = props;
    const isSidebar = variant === 'sidebar';

    const [state, setState] = useControllableValue<string>(props, {
        defaultValue: '',
        valuePropName: 'searchQuery',
        trigger: 'onSearchQueryChange',
    });

    return (
        <div className={isLoading ? 'opacity-50' : undefined}>
            <div className="flex flex-col items-center justify-center gap-4 md:justify-between">
                <div className="w-full grow">
                    <RoccoInput
                        color={isSidebar ? 'gray' : 'outline'}
                        text="small"
                        shape={isSidebar ? 'default' : 'rounded'}
                        className={
                            isSidebar
                                ? cn(
                                      'h-[2.625rem] rounded-[0.25rem] px-[0.5625rem] text-[1rem] placeholder:text-[#717171]',
                                      isDark
                                          ? 'bg-white/10 text-white placeholder:text-white/40'
                                          : 'bg-[#f4f4f4]',
                                  )
                                : undefined
                        }
                        placeholder={searchInputPlaceholder}
                        value={state}
                        showClearButton={true}
                        postfix={<Search className="size-4.5" />}
                        onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                            setState(evt.target.value);
                        }}
                    />
                </div>

                {groupAlphabetically && onGroupingChange && (
                    <div className="scrollbar-hidden w-[85dvw] overflow-x-scroll md:w-unset md:max-w-[320px]">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                className={cn(
                                    'min-w-4 text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                    !groupedBy
                                        ? 'text-gray-900 underline'
                                        : undefined,
                                )}
                                onClick={() => onGroupingChange()}
                            >
                                All
                            </button>

                            {Object.entries(FilterGroupOptions).map(
                                ([key, label]) => {
                                    const k = key as keyof FilterGroupOptions;

                                    return (firstCharacterGroups?.[k] ?? 0) >
                                        0 ? (
                                        <button
                                            key={k}
                                            className={cn(
                                                'text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                                groupedBy && groupedBy === k
                                                    ? 'text-gray-900 underline'
                                                    : undefined,
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                            onClick={() => onGroupingChange(k)}
                                        >
                                            {label}
                                        </button>
                                    ) : (
                                        <div
                                            key={k}
                                            className={cn(
                                                'min-w-3 cursor-not-allowed text-center text-gray-500/30',
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                        >
                                            {label}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={isSidebar ? 'mt-3' : 'mt-6'}>{children}</div>
        </div>
    );
};
